import {LoadingButton} from '@mui/lab';
import {
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Icon,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import {useFormik} from 'formik';
import {useSnackbar} from 'notistack';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import server from '../../../global/server';
import {useFetch} from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import {Check, Close, CloseRounded} from '@mui/icons-material';
import * as Yup from 'yup';
import ProfessionalForm from '../components/ProfessionalForm';
import {useConfirm} from 'material-ui-confirm';

const validationSchema = Yup.object().shape({
    holder_name: Yup.string().required('Campo obrigatório'),
    document: Yup.string().required('Campo obrigatório'),
    document_type: Yup.string().required('Campo obrigatório'),
    email: Yup.string().required('Campo obrigatório'),
});

export default () => {
    const navigation = useNavigate();
    const {data, mutate, isLoading} = useFetch('/professional');
    const [errs, setErrs] = useState();
    const [viewDialog, setViewDialog] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    useHandleErrors(errs);
    const confirm = useConfirm();
    const {
        handleChange,
        handleSubmit,
        handleReset,
        setValues,
        setFieldValue,
        values,
        errors,
        isSubmitting,
    } = useFormik({
        initialValues: {
            status: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values, {setSubmitting}) => {
            try {
                setSubmitting(true);

                if (values.id) {
                    const {data} = await server.put('/professional', values);
                    enqueueSnackbar('Profissional alterado');
                } else {
                    const {data} = await server.post('/professional', values);
                    enqueueSnackbar('Profissional adicionado');
                }
                setSubmitting(false);
                await mutate();
                handleClose();
            } catch (error) {
                setSubmitting(false);
                setErrs(error);
            }
        },
    });

    const handleDelete = async id => {
        try {
            await server.delete('/professional', {data: {id}});
            await mutate();
            handleReset();
            enqueueSnackbar('Validação realizada');
            setViewDialog(false);
        } catch (error) {
            setErrs(error);
        }
    };

    const columns = useMemo(() => [
        {field: 'id', headerName: 'ID', width: 50},
        {
            field: 'User',
            headerName: 'Nome',
            flex: 1,
            minWidth: 250,
            valueGetter: (value) => value?.Person?.name || 'Usuário removido',
        },
        {
            field: 'professional_number',
            headerName: 'Registro Profissional',
            flex: 1,
        },
        {
            field: 'User.Person.email',
            headerName: 'Email',
            flex: 1,
            valueGetter: (value, row) => row.User?.Person?.email || 'Usuário removido',
        },
        {
            field: 'verified_at',
            headerName: 'Verificado',
            type: 'boolean',
            valueGetter: (value) => Boolean(value), //(Boolean(value) ? 'Sim' : 'Não'),
           // renderCell: ({value}) => (value === 'Sim' ? <Check /> : <CloseRounded />),
        },
        {
            field: 'approved_at',
            headerName: 'Aprovado',
            type: 'boolean',
            valueGetter: (value) =>Boolean(value),// (Boolean(value) ? 'Sim' : 'Não'),
            //renderCell: ({value}) => (value === 'Sim' ? <Check /> : <CloseRounded />),
        },
        {
            field: 'actions',
            type: 'actions',
            renderCell: ({row}) => (
                <Button
                    size="small"
                    onClick={e => {
                        setValues({...row, ...row.User?.Person, ...row.Professional}, true);
                        setViewDialog(true);
                    }}>
                    Visualizar
                </Button>
            ),
        },
    ]);

    const handleClose = () => {
        setViewDialog(false);
        handleReset();
    };

    const handleValidate = async professional_id => {
        try {
            await confirm({
                title: 'Validando cadastro',
                description:
                    'A validação do cadastro libera o cadastro para onboarding. Você quer continuar?',
            });

            setActionLoading(true);
            const {data} = await server.put('professional/verify', {professional_id});

            enqueueSnackbar(data?.message || 'Cadastro validado', {variant: 'success'});
            await mutate();
            setViewDialog(false);
            handleReset();
        } catch (error) {
            setErrs(error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRatification = async professional_id => {
        try {
            await confirm({
                title: 'Integrando operador',
                description:
                    'Você está conectando o profissional ao integrador de prescrição MEMED?',
            });

            setActionLoading(true);
            const {data} = await server.put('professional/ratification', {professional_id});

            enqueueSnackbar(data?.message || 'Memed integrada', {variant: 'success'});
            await mutate();
            setViewDialog(false);
            handleReset();
        } catch (error) {
            setErrs(error);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Container fixed>
            <Stack direction="row" alignItems="center" justifyContent="space-between" my={3}>
                <Typography variant="h5" component="h2">
                    Profissionais
                </Typography>

                {/* <Stack direction="row" alignItems="center" columnGap={2}>
                    <Button variant="contained" onClick={e => setViewDialog(true)}>
                        Adicionar Profissional
                    </Button>
                </Stack> */}
            </Stack>
            <DataGrid autoHeight loading={isLoading} rows={data || []} columns={columns} />
            <Dialog
                components="form"
                maxWidth="md"
                fullWidth
                open={viewDialog}
                onClose={e => {
                    handleReset();
                    setViewDialog(false);
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <DialogTitle>Dados do profissional</DialogTitle>
                    <Stack direction="row" columnGap={2}>
                        {Boolean(values.id) && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={actionLoading}
                                    onClick={() => handleValidate(values.id)}>
                                    {!Boolean(values.verified_at)
                                        ? 'Validar cadastro'
                                        : 'Reenviar validação'}
                                </Button>
                            </>
                        )}

                        {Boolean(values.id)   && (
                            <>
                                <Button
                                    disabled={
                                        actionLoading ||
                                        Boolean(values.approved_at) ||
                                        !Boolean(values.completed_at)
                                    }
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleRatification(values.id)}>
                                    Integrar Memed
                                </Button>
                            </>
                        )}
                        <IconButton
                            onClick={() => {
                                setViewDialog(false);
                                handleReset();
                            }}>
                            <Close />
                        </IconButton>
                    </Stack>
                </Stack>
                <Divider />
                <DialogContent dividers sx={{p: 0}}>
                    <ProfessionalForm
                        values={values}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        errors={errors}
                    />
                </DialogContent>

                {/*<DialogActions> */}
                {/* {Boolean(values.id) && (
                        <>
                            <Button
                                color="error"
                                onClick={e =>
                                    window.confirm('Você tem certeza?') && handleDelete(values.id)
                                }>
                                Excluir
                            </Button>
                        </>
                    )} */}
                {/* <Button onClick={e => setViewDialog(false)}>Cancelar</Button> */}
                {/* <LoadingButton loading={isSubmitting} onClick={handleSubmit}>
                        Salvar
                    </LoadingButton> */}
                {/* </DialogActions> */}
            </Dialog>
        </Container>
    );
};
